import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Fuel, Loader2, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | invalid
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setStatus("invalid"); return; }
      try {
        const { data } = await api.get(`/invitations/${token}`);
        setInvite(data);
        setUsername(data.email.split("@")[0]);
        setStatus("ready");
      } catch {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/invitations/accept", { token, username, password });
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-6">
      <div className="w-full max-w-sm animate-fadeUp">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Fuel className="w-6 h-6 text-black" />
          </div>
          <span className="font-[Outfit] font-bold text-2xl text-white">Fuel Tracer</span>
        </div>

        {status === "loading" && <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />}

        {status === "invalid" && (
          <div data-testid="invite-invalid">
            <h2 className="font-[Outfit] font-bold text-2xl text-white mb-2">Invitation not valid</h2>
            <p className="text-[#9CA3AF] text-sm">This invite link is invalid or has already been used.</p>
            <Link to="/login" className="inline-block mt-6 text-emerald-400 text-sm hover:underline">Go to sign in →</Link>
          </div>
        )}

        {status === "ready" && (
          <form onSubmit={submit} data-testid="accept-invite-form">
            <div className="flex items-center gap-2 text-emerald-400 text-sm mb-2">
              <CheckCircle2 className="w-4 h-4" /> You're invited as {invite.role.replace("_", " ")}
            </div>
            <h2 className="font-[Outfit] font-bold text-2xl text-white mb-1">Welcome, {invite.name}</h2>
            <p className="text-[#9CA3AF] text-sm mb-6">Set a username and password for <b className="text-white">{invite.email}</b>.</p>
            {error && <div className="mb-4 text-sm text-red-400">{error}</div>}
            <div className="space-y-4">
              <div>
                <Label className="text-[#9CA3AF]">Username</Label>
                <Input data-testid="invite-username-input" value={username} onChange={(e) => setUsername(e.target.value)}
                  required className="mt-1.5 bg-[#111827] border-[#374151] text-white" />
              </div>
              <div>
                <Label className="text-[#9CA3AF]">Password</Label>
                <Input type="password" data-testid="invite-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                  required minLength={6} className="mt-1.5 bg-[#111827] border-[#374151] text-white" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="invite-submit-button"
              className="w-full mt-6 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account & continue"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
